import { isNil } from 'lodash'

/**
 * Двухбуквенные коды стран по стандарту `ISO 3166-1 alpha-2`
*/
export type CountryCodeISO = 'RU' | 'KZ' | 'BY' | 'UZ' | 'AZ'

interface PhoneInfo {
    code: string
    mask: string
    validationRegExp: RegExp
}

export const intlPhoneSettings: { [key in CountryCodeISO]: PhoneInfo } = {
    RU: {
        code: '7',
        mask: '(___) ___-__-__',
        validationRegExp: /^(\+7)?\d{10}$/,
    },
    KZ: {
        code: '7',
        mask: '(___) ___-__-__',
        validationRegExp: /^(\+7)?\d{10}$/,
    },
    BY: {
        code: '375',
        mask: '(__) ___-__-__',
        validationRegExp: /^(\+375)?\d{9}$/,
    },
    UZ: {
        code: '998',
        mask: '(__) ___-__-__',
        validationRegExp: /^(\+998)?\d{9}$/,
    },
    AZ: {
        code: '994',
        mask: '(__) ___-__-__',
        validationRegExp: /^(\+994)?\d{9}$/,
    }
}

/**
 * Телефонные коды стран
 */
const phoneCountryCodes: { [key: string]: CountryCodeISO | CountryCodeISO[] } = {
    '7': ['RU', 'KZ'],
    '375': 'BY',
    '998': 'UZ',
    '994': 'AZ',
}

export const getCountryCodeByPhone = (
    phone: string,
    /**
     * Если по коду телефона есть выбор из нескольких стран (например +7 это RU и KZ),
     * то указав этот параметр будет, по возможности, будет отдано предпочтение этой стране.
     *
     * @example
     * getCountryCodeByPhone('+79009998844') => 'RU' (т.к. он первый в списке phoneCountryCodes для кода '7')
     * getCountryCodeByPhone('+79009998844', 'KZ') => 'KZ'
     */
    preferredCountryCode?: CountryCodeISO,
): CountryCodeISO => {
    const phoneWithoutPlus: string = phone.replace('+', '')

    let countryCode: CountryCodeISO | CountryCodeISO[] = null

    for (let i = 1; i <= phoneWithoutPlus.length; i += 1) {
        const pred = phoneWithoutPlus.slice(0, i)

        if (phoneCountryCodes[pred]) {
            countryCode = phoneCountryCodes[pred]

            break
        }
    }

    if (isNil(countryCode)) {
        return null
    }

    if (typeof countryCode !== 'string') {
        countryCode = preferredCountryCode && countryCode.includes(preferredCountryCode)
            ? preferredCountryCode
            : countryCode[0]
    }

    return countryCode
}

/**
 * Форматирует
 * @param phone неотформатированный номер телефона
 * @returns номер телефона отформатированный в зависимости от страны
 * @example
 * // Россия/Казахстан
 * formatPhoneToFront('+79047082264') => '+7 (904) 708-22-64'
 * // Белоруссия
 * formatPhoneToFront('+375173274562') => '+375 (17) 327-45-62'
 * // Неопределенный
 * formatPhoneToFront('+555111234978874') => '+555111234978874'
 */
export const formatPhone = (phone: string): string => {
    const countryCode = getCountryCodeByPhone(phone)

    if (isNil(countryCode)) {
        return phone
    }

    const phoneWithoutCountryCode = phone.slice(intlPhoneSettings[countryCode].code.length + 1)

    const { code, mask } = intlPhoneSettings[countryCode]

    let result: string = `+${code} ${mask}`

    phoneWithoutCountryCode
        .split('')
        .forEach(symbol => {
            result = result.replace('_', symbol)
        })

    return result
}

/**
 * Удаляет код страны из номера телефона
 * @param phone номер телефона вида `+79047082264`
 * @returns номер телефона без кода страны
 * @example
 * // Россия/Казахстан
 * phoneWithoutCountryCode('+79047082264') => '9047082264'
 * // Белоруссия
 * phoneWithoutCountryCode('+375173274562') => '173274562'
 */
export const phoneWithoutCountryCode = (phone: string): string => {
    const country = getCountryCodeByPhone(phone)

    return phone.replace(`+${intlPhoneSettings[country].code}`, '')
}

/**
 * Добавляет к номеру телефона код страны
 * @param phone номер телефона без кода страны вида `9047082264`
 * @param country код страны по стандарту `ISO 3166-1 alpha-2`
 * @returns номер телефона без кода страны
 * @example
 * phoneWithCountryCode('9047082264', 'RU') => '+79047082264'
 * phoneWithCountryCode('173274562', 'BY') => '+375173274562'
 */
export const phoneWithCountryCode = (phone: string, country: CountryCodeISO): string => {
    return `+${intlPhoneSettings[country].code}${phone}`
}

/**
 * Меняет у номера телефона код страны
 *
 * Если у новой страны номер телефона короче, то подрезает последние цифры (напр. когда меняем с России на Беларусь)
 * @param phone номер телефона вида `+79047082264`
 * @param country код страны по стандарту `ISO 3166-1 alpha-2`
 * @returns номер телефона с измененным кодам страны
 * @example
 * // Россия -> Белaрусь
 * changePhoneCountry('+79047082264', 'BY') => '+375904708226'
 * // Белaрусь -> Казахстан
 * changePhoneCountry('+375904708226', 'BY') => '+7904708226'
 */
export const changePhoneCountry = (phone: string, country: CountryCodeISO): string => {
    const cuting = (phone: string, code: CountryCodeISO): string => {
        const INVALID_SYMBOLS_REGEXP: RegExp = /-|\(|\)|\s/g

        const mask = intlPhoneSettings[code].mask
        const length = mask.replace(INVALID_SYMBOLS_REGEXP, '').length

        if (phone.length > length) {
            return phone.substring(0, length)
        }

        return phone
    }

    const withoutCode = phoneWithoutCountryCode(phone)
    const cutted = cuting(withoutCode, country)
    const withCode = phoneWithCountryCode(cutted, country)

    return withCode
}
